/**
 * Business Review API helpers (backend/routes/review.py).
 *
 *   const wbr = useReviewData('wbr', { country, week, platform, brand, category });
 *   const availability = useReviewData(tab === 'availability' ? 'availability' : null, { country, week, platform });
 *
 *   path    'coverage' | 'wbr' | 'availability' | 'profit' | 'selection'; a falsy path means idle.
 *   params  query parameters; null / '' / 'ALL' are dropped.
 * Returns { data, loading, error, refresh } like useSalesData: `data` keeps the last good response
 * while the next one loads and the previous request is aborted on every URL change.
 *
 * Weeks run Monday to Sunday (India time). weekStart(day) -> the Monday of that week; shiftWeek(monday, n).
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../contexts/AuthContext';
import { indiaToday, shiftDay } from '../sales/format';

const isEmpty = value => value == null || value === '' || value === 'ALL';

export const reviewUrl = (path, params = {}) => {
  const query = new URLSearchParams();
  Object.keys(params).sort().forEach(key => { if (!isEmpty(params[key])) query.set(key, String(params[key])); });
  const text = query.toString();
  return `${process.env.REACT_APP_BACKEND_URL || ''}/api/review/${path}${text ? `?${text}` : ''}`;
};

export const useReviewData = (path, params = {}) => {
  const url = path ? reviewUrl(path, params) : null;
  const [state, setState] = useState({ data: null, loading: Boolean(url), error: null });
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion(value => value + 1), []);

  useEffect(() => {
    if (!url) { setState(prev => (prev.loading || prev.error ? { ...prev, loading: false, error: null } : prev)); return undefined; }
    const controller = new AbortController();
    setState(prev => ({ ...prev, loading: true, error: null }));
    axios.get(url, { headers: getAuthHeaders(), signal: controller.signal, timeout: 120000 })
      .then(response => { if (!controller.signal.aborted) setState({ data: response.data, loading: false, error: null }); })
      .catch(reason => {
        if (controller.signal.aborted || axios.isCancel?.(reason)) return;
        setState(prev => ({ data: prev.data, loading: false, error: reason || new Error('The review could not be loaded.') }));
      });
    return () => controller.abort();
  }, [url, version]);

  return { ...state, refresh, url };
};

/** Monday of the Monday–Sunday week holding `day` (YYYY-MM-DD). */
export const weekStart = day => {
  const date = new Date(`${day}T12:00:00Z`);
  const offset = (date.getUTCDay() + 6) % 7; // Monday = 0
  return shiftDay(day, -offset);
};

export const shiftWeek = (monday, weeks) => shiftDay(monday, 7 * weeks);

/** The last complete week's Monday (a week is complete once its Sunday is before today in India). */
export const lastCompleteWeek = (now = new Date()) => shiftWeek(weekStart(indiaToday(now)), -1);

export const isCompleteWeek = (monday, now = new Date()) => shiftDay(monday, 6) < indiaToday(now);

export const weekLabel = monday => {
  const sunday = shiftDay(monday, 6);
  const fmt = day => new Date(`${day}T12:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return `${fmt(monday)} – ${fmt(sunday)} ${sunday.slice(0, 4)}`;
};

export const TAG_LABELS = {
  stock_out: 'Stock-out', price_up: 'Price up', price_down: 'Price down', ads_up: 'Ads up', ads_down: 'Ads down',
  returns_rise: 'Returns up', new_listing: 'New listing', delisted_likely: 'Delisted?', seasonal: 'Seasonal', demand: 'Demand',
};
export const TAG_TONES = {
  stock_out: 'bg-red-50 text-red-800 ring-red-200', price_down: 'bg-amber-50 text-amber-800 ring-amber-200',
  price_up: 'bg-amber-50 text-amber-800 ring-amber-200', ads_up: 'bg-violet-50 text-violet-800 ring-violet-200',
  ads_down: 'bg-violet-50 text-violet-800 ring-violet-200', returns_rise: 'bg-red-50 text-red-800 ring-red-200',
  new_listing: 'bg-sky-50 text-sky-800 ring-sky-200', delisted_likely: 'bg-gray-100 text-gray-800 ring-gray-300',
  seasonal: 'bg-green-50 text-green-800 ring-green-200', demand: 'bg-gray-100 text-gray-700 ring-gray-200',
};

export const ACTION_LABELS = {
  pause_ads_oos: 'Pause ads', reorder_now: 'Reorder now', fix_listing: 'Fix listing', investigate_returns: 'Check returns',
  review_price: 'Review price', liquidate_aged: 'Clear aged stock',
};
